import { useEffect, useMemo, useState } from 'react';

import { FarIcons } from '@playbooks/icons';
import { SearchField } from '@playbooks/molecules/forms';
import { StructuralIcon } from '@playbooks/ui/components/structural-icon';

import { WorkspaceMcpFlow } from 'components/agent/workspace-mcp-flow';
import {
	ComposerActionFeedback,
	ComposerActionHeader,
	ComposerActionListSkeleton,
	ComposerActionRow,
	ComposerActionSection,
} from 'components/composer/composer-actions-components';
import { useProject, useStore, useWorkspace } from 'contexts';
import { useInit } from 'hooks';
import { FlowBody } from 'utils/flow';
import { projectResourcePayload } from 'utils/project-resources';

const ComposerActionsMcps = ({
	type,
	disabled,
	canManageServers,
	preferences: draftPreferences,
	onChange,
	onNext,
	onPrev,
}) => {
	const [mcps, setMcps] = useState([]);
	const [query, setQuery] = useState('');
	const [flowOpen, setFlowOpen] = useState(false);
	const [flowView, setFlowView] = useState('select');
	const project = useProject();
	const store = useStore();
	const workspace = useWorkspace();
	const available = Boolean(workspace.workspace?.id && workspace.rootApi);
	const interactionDisabled = disabled || !available;
	const draftMode = type === 'app';
	const preferences = draftMode ? draftPreferences || {} : {};
	const selected = draftMode ? preferences.mcps || [] : (project.project?.mcps || []).map(item => item.mcp);
	const rootApi = type === 'project' ? `${project?.rootApi}/resources` : null;
	const ordered = useMemo(() => {
		const selectedIds = new Set(selected.map(item => item.id));
		return [...selected, ...mcps]
			.filter((item, index, values) => values.findIndex(value => value.id === item.id) === index)
			.sort((a, b) => Number(selectedIds.has(b.id)) - Number(selectedIds.has(a.id)));
	}, [mcps, selected]);

	const [fetchMcps, loading, error] = useInit(async () => {
		if (!available) return;
		const response = await store.query({
			url: `${workspace.rootApi}/mcps`,
			params: { pageSize: 12, ...(query.trim() ? { query: query.trim() } : {}) },
		});
		const records = response.data || [];
		setMcps(records);
		if (!records.length) return;
		const health = await store.createRecord({
			url: `${workspace.rootApi}/mcps/health`,
			data: { ids: records.map(item => item.id) },
		});
		setMcps(health.data || records);
	});

	useEffect(() => {
		if (!available) return;
		const timeout = setTimeout(() => fetchMcps(), query ? 200 : 0);
		return () => clearTimeout(timeout);
	}, [available, query, store]);

	const onSave = async (values, finish = false) => {
		if (interactionDisabled) return;
		const formatted = values.map((item, position) => ({ ...item, id: item.id, position }));
		if (draftMode) return onChange?.({ ...preferences, mcps: formatted });
		const response = await store.updateRecord({
			url: rootApi,
			data: projectResourcePayload(project.project, { workspaceMcpIds: formatted.map(item => item.id) }),
		});
		project.onUpdate(response.data);
		if (finish) onNext?.();
	};
	const onSelect = mcp =>
		mcp.health !== 'available' && !selected.some(item => item.id === mcp.id)
			? undefined
			: onSave(
					selected.some(item => item.id === mcp.id) ? selected.filter(item => item.id !== mcp.id) : [...selected, mcp],
				);
	const onFlow = view => {
		setFlowView(view);
		setFlowOpen(true);
	};
	const renderMcp = mcp => {
		const active = selected.some(item => item.id === mcp.id);
		const selectable = active || mcp.health === 'available';
		return (
			<ComposerActionRow
				key={mcp.id}
				label={mcp.name}
				value={mcp.health === 'available' ? 'Available' : mcp.health === 'unavailable' ? 'Unavailable' : 'Not checked'}
				selected={active}
				disabled={interactionDisabled || !selectable}
				trailing={
					<StructuralIcon
						icon={active ? FarIcons.farCircleCheck : FarIcons.farChevronRight}
						className={active ? 'size-3.5 text-success' : 'size-3 text-foreground-body'}
						aria-hidden
					/>
				}
				onClick={() => onSelect(mcp)}
			/>
		);
	};

	return (
		<>
			<ComposerActionHeader onPrev={onPrev}>
				<SearchField
					value={query}
					placeholder='Search MCPs...'
					className='h-8 grow'
					onChange={event => setQuery(event.target.value)}
					onClear={() => setQuery('')}
				/>
			</ComposerActionHeader>
			<FlowBody>
				{!available ? (
					<ComposerActionFeedback
						icon='server'
						title='Sign in to manage MCPs'
						text='Workspace MCPs become available after sign in.'
					/>
				) : loading ? (
					<ComposerActionListSkeleton count={5} />
				) : error ? (
					<ComposerActionFeedback
						icon='triangle-exclamation'
						title='MCPs unavailable'
						text={error?.message || 'MCPs could not be loaded.'}
					/>
				) : ordered.length ? (
					<ComposerActionSection>{ordered.slice(0, 12).map(renderMcp)}</ComposerActionSection>
				) : (
					<ComposerActionFeedback
						icon='server'
						title='No MCPs available'
						text='Add an MCP server to use it with agents.'
					/>
				)}
				<ComposerActionSection>
					<ComposerActionRow label='View More' disabled={interactionDisabled} onClick={() => onFlow('select')} />
					<ComposerActionRow
						label='Add New'
						disabled={interactionDisabled || !canManageServers}
						onClick={() => onFlow('form')}
					/>
				</ComposerActionSection>
			</FlowBody>
			<WorkspaceMcpFlow
				open={flowOpen}
				setOpen={setFlowOpen}
				initialView={flowView}
				selected={selected}
				canManageServers={canManageServers}
				onSave={onSave}
				onCreate={
					draftMode
						? async resource => ({
								...resource,
								id: `pending-mcp-${Date.now()}`,
								pendingResource: resource,
							})
						: undefined
				}
			/>
		</>
	);
};

export { ComposerActionsMcps };
